import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReportFormat } from '@/application/metrics';
import { canExportReports } from '@/application/permissions/canExportReports';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';

interface ExportButtonsProps {
  /** Dispara la exportación del formato pedido (descarga server-side). */
  onExport: (format: ReportFormat) => Promise<void>;
  disabled?: boolean;
  /** Sin label interno (el padre ya muestra "Exportar"). */
  compact?: boolean;
  /** Título visible del control (también en compact). */
  label?: string;
  /** Área táctil ≥44px en mobile (hub / CTAs de dueño). */
  touchFriendly?: boolean;
  formats?: ReportFormat[];
}

const DEFAULT_FORMATS: ReportFormat[] = ['pdf', 'xlsx'];

/** Barra de descarga de reportes (PDF/XLSX/CSV) con estado de progreso y error. */
export function ExportButtons({
  onExport,
  disabled,
  compact = false,
  label,
  touchFriendly = false,
  formats = DEFAULT_FORMATS,
}: ExportButtonsProps) {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [exporting, setExporting] = useState<ReportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canExportReports(hasPermission)) {
    return null;
  }

  async function handleExport(format: ReportFormat) {
    setExporting(format);
    setError(null);
    try {
      await onExport(format);
    } catch (err) {
      const isTimeout = err instanceof Error && err.message === 'export_timeout';
      setError(t(isTimeout ? 'metrics.export.timeout' : 'metrics.export.error'));
    } finally {
      setExporting(null);
    }
  }

  const busy = exporting !== null;
  const shownLabel = label ?? (!compact ? t('metrics.export.label') : null);

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${compact ? 'justify-end' : ''}`}
      role={shownLabel ? 'group' : undefined}
      aria-label={shownLabel ?? undefined}
    >
      {shownLabel ? (
        <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
          {shownLabel}
        </span>
      ) : null}
      {formats.map((format) => (
        <button
          key={format}
          type="button"
          disabled={disabled || busy}
          onClick={() => handleExport(format)}
          className={
            compact
              ? touchFriendly
                ? 'btn btn-secondary min-h-11 px-3'
                : 'btn btn-secondary'
              : 'btn btn-secondary btn-sm'
          }
        >
          {exporting === format ? t('metrics.export.exporting') : t(`metrics.export.${format}`)}
        </button>
      ))}
      {error && (
        <span role="alert" className="text-xs text-red-600 dark:text-red-400">
          {error}
        </span>
      )}
    </div>
  );
}
